import { RAI } from 'types/Concessionnaire';
import { numberToLocaleString } from 'utils/number';

export const REPARTITION_MAX = 20;

export type SliderValue = number | [number, number];

export interface Step3PersonneMoraleViewModel {
  primeParVehiculeValue: number;
  showSlider: boolean;
  shouldShowCompactResults: boolean;
  volumeMwhc: string;
  totalVolumeMwhc: string;
  concessionnairePercent: number;
  beneficiairePercent: number;
  primeBeneficiaire: number;
  primeConcessionnaire: number;
  totalPrimeBeneficiaire: number;
  totalPrimeConcessionnaire: number;
  totalPrime: number;
  maxPrimeConcessionnairePerVehicle: number;
  minPrimeBeneficiaire: number;
}

interface BuildStep3PersonneMoraleViewModelArgs {
  rai?: RAI;
  primeParVehicule?: number;
  volume?: number;
  nbVehicules: number;
  repartition: number;
}

export function clampConcessionnairePercent(value: number) {
  return Math.min(REPARTITION_MAX, Math.max(0, value));
}

export function clampBeneficiairePercent(value: number) {
  return Math.min(100, Math.max(100 - REPARTITION_MAX, value));
}

export function normalizeBeneficiaireRepartition(value: unknown) {
  if (value === undefined || value === null || value === '') {
    return 100;
  }

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return 100;
  }

  return clampBeneficiairePercent(numericValue);
}

export function normalizeSliderValue(value: SliderValue) {
  return Array.isArray(value) ? value[0] : value;
}

export function buildStep3PersonneMoraleViewModel({
  rai,
  primeParVehicule,
  volume,
  nbVehicules,
  repartition,
}: BuildStep3PersonneMoraleViewModelArgs): Step3PersonneMoraleViewModel {
  const primeParVehiculeValue = Number(primeParVehicule) || 0;
  const showSlider = rai === '3.1';
  const shouldShowCompactResults = !showSlider;
  const volumeMwhc = volume ? numberToLocaleString(volume / 1000, 'MWhc') : '0';
  const totalVolume = Number(volume) * (nbVehicules / 1000);
  const totalVolumeMwhc = totalVolume
    ? numberToLocaleString(totalVolume, 'MWhc')
    : '0';
  const concessionnairePercent = clampConcessionnairePercent(100 - repartition);
  const beneficiairePercent = clampBeneficiairePercent(repartition);
  const primeBeneficiaire = (primeParVehiculeValue * beneficiairePercent) / 100;
  const primeConcessionnaire = primeParVehiculeValue - primeBeneficiaire;
  const totalPrimeBeneficiaire = primeBeneficiaire * nbVehicules;
  const totalPrimeConcessionnaire = primeConcessionnaire * nbVehicules;
  const totalPrime = primeParVehiculeValue * nbVehicules;
  const maxPrimeConcessionnairePerVehicle =
    (primeParVehiculeValue * REPARTITION_MAX) / 100;
  const minPrimeBeneficiaire =
    primeParVehiculeValue - maxPrimeConcessionnairePerVehicle;

  return {
    primeParVehiculeValue,
    showSlider,
    shouldShowCompactResults,
    volumeMwhc,
    totalVolumeMwhc,
    concessionnairePercent,
    beneficiairePercent,
    primeBeneficiaire,
    primeConcessionnaire,
    totalPrimeBeneficiaire,
    totalPrimeConcessionnaire,
    totalPrime,
    maxPrimeConcessionnairePerVehicle,
    minPrimeBeneficiaire,
  };
}
